import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../lib/config';
import { toast } from 'sonner';

// Both bells use the same server-owned read/delete state. No local archive.
export default function useNotificationInbox(open) {
  const { user } = useAuth();
  const userId = user?.id;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [limit, setLimit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);
  const mutating = useRef(false);
  const currentUser = useRef(userId);
  currentUser.current = userId;

  const refresh = useCallback(async () => {
    const requestId = ++sequence.current;
    if (!userId) { setLoading(false); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/notifications/in-app?limit=${limit}&unread_only=${unreadOnly}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('load');
      const data = await res.json();
      if (requestId !== sequence.current || currentUser.current !== userId) return;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      if (navigator.setAppBadge) {
        try {
          const badgeUpdate = data.unread_count > 0
            ? navigator.setAppBadge(data.unread_count)
            : navigator.clearAppBadge?.();
          badgeUpdate?.catch(() => {});
        } catch { /* Badge support is optional */ }
      }
      setReadCount(data.read_count || 0);
      setHasMore(!!data.has_more);
      setError(false);
    } catch {
      if (requestId === sequence.current && currentUser.current === userId) setError(true);
    } finally {
      if (requestId === sequence.current && currentUser.current === userId) setLoading(false);
    }
  }, [userId, unreadOnly, limit]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    setReadCount(0);
    setHasMore(false);
    setUnreadOnly(false);
    setLimit(50);
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    refresh();
    const visibleRefresh = () => {
      if (document.visibilityState === 'visible' && !mutating.current) refresh();
    };
    const interval = setInterval(visibleRefresh, 30000);
    const onPush = (event) => {
      if (event.data?.type === 'TRAINNOVA_NOTIFICATION_RECEIVED') visibleRefresh();
    };
    window.addEventListener('focus', visibleRefresh);
    document.addEventListener('visibilitychange', visibleRefresh);
    navigator.serviceWorker?.addEventListener('message', onPush);
    return () => {
      ++sequence.current;
      clearInterval(interval);
      window.removeEventListener('focus', visibleRefresh);
      document.removeEventListener('visibilitychange', visibleRefresh);
      navigator.serviceWorker?.removeEventListener('message', onPush);
    };
  }, [refresh, open]);

  const mutate = async (path, method) => {
    if (!userId || mutating.current) return false;
    mutating.current = true;
    setBusy(true);
    ++sequence.current; // An older GET must not overwrite the mutation result.
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/notifications/in-app${path}`, {
        method, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('mutation');
      if (currentUser.current !== userId) return false;
      await refreshRef.current();
      return true;
    } catch {
      if (currentUser.current === userId) toast.error('Kunde inte uppdatera notiserna. Försök igen.');
      return false;
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  };

  return {
    notifications, unreadCount, readCount, unreadOnly, loading, error, busy,
    refresh,
    setUnreadOnly: (value) => { setLimit(50); setUnreadOnly(value); },
    hasMore: hasMore && limit < 500,
    loadMore: () => setLimit(value => Math.min(value + 50, 500)),
    markRead: (id) => mutate(`/${encodeURIComponent(id)}/read`, 'POST'),
    markAllRead: () => mutate('/read-all', 'POST'),
    remove: (id) => mutate(`/${encodeURIComponent(id)}`, 'DELETE'),
    clearRead: () => mutate('?read_only=true', 'DELETE'),
  };
}
